import {DateTimeFormat} from '@hconnect/common/components/dateTime/DateTimeFormat'
import {DateTime, SapDataDto, SapStatusCode} from '@hconnect/common/types'
import {Typography} from '@mui/material'
import {AxiosError} from 'axios'
import moment, {Moment} from 'moment-timezone'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {useSapWorkOrders} from '../../hooks/api/useSapWorkOrder'

export const hasMaintenanceNotificationCreationTimedOut = (item: SapDataDto, now: Moment) =>
  !item.sapMaintenanceNotificationId &&
  Boolean(item.lastMaintenanceNotificationCreationStart) &&
  (!item.sapStatusCode || item.sapStatusCode === SapStatusCode.SUBMITTED) &&
  (item.lastMaintenanceNotificationCreationStart
    ? moment(item.lastMaintenanceNotificationCreationStart).clone().add(10, 'minutes').isBefore(now)
    : undefined)

type InfoProps = {
  label?: string
  timezone: string
  date?: DateTime | undefined
  userName?: string
  'data-test-id'?: string
  component?: React.ElementType
}

export const Info: React.FC<InfoProps> = ({
  label,
  userName,
  timezone,
  date,
  'data-test-id': dataTestId,
  component
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  return (
    <Typography
      component={component ?? 'p'}
      gutterBottom
      variant="caption"
      data-test-id={dataTestId}
    >
      {label && `${label} `}
      {userName && date && (
        <>
          {userName} {t('machineSentryActions.happenOn')}{' '}
          <DateTimeFormat date={date} timezone={timezone} />
        </>
      )}
    </Typography>
  )
}

export const MachineSentrySapMaintenanceNotificationDetails = ({
  item,
  notificationFailed,
  timezone,
  plantId
}: {
  item: SapDataDto
  notificationFailed: boolean | undefined
  timezone: string
  plantId: string
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const sapMaintenanceNotificationId = item.sapMaintenanceNotificationId
  const hasNotificationCreationInfo =
    item.sapMaintenanceNotificationCreator?.name && item.lastMaintenanceNotificationCreationStart

  return (
    <>
      {(hasMaintenanceNotificationCreationTimedOut(item, moment.tz(timezone)) ||
        notificationFailed) && (
        <>
          <Typography
            component="p"
            pt={0.5}
            variant="caption"
            color="error"
            data-test-id="event-info-sap-notification-error"
          >
            {t('machineSentryActions.maintenanceNotificationCreationTimeout')}{' '}
          </Typography>
          {hasNotificationCreationInfo && (
            <Info
              component="span"
              label={t('machineSentryActions.sentBy')}
              timezone={timezone}
              userName={item.sapMaintenanceNotificationCreator?.name}
              date={item.lastMaintenanceNotificationCreationStart}
              data-test-id="event-info-sap-notification-error-creator-info"
            />
          )}
        </>
      )}
      {sapMaintenanceNotificationId && (
        <Info
          label={`${t(
            'tasks.machineSentryActions.sapPmNotificationId'
          )}: ${sapMaintenanceNotificationId} ${
            hasNotificationCreationInfo ? t('machineSentryActions.sentBy') : ''
          }`}
          timezone={timezone}
          data-test-id="event-info-sap-notification-label"
          userName={item.sapMaintenanceNotificationCreator?.name}
          date={item.lastMaintenanceNotificationCreationStart}
        />
      )}
      {sapMaintenanceNotificationId && (
        <WorkOrders plantId={plantId} notificationId={sapMaintenanceNotificationId}></WorkOrders>
      )}
    </>
  )
}

const WorkOrdersError: React.FC<{error: AxiosError | null}> = ({error}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  if (!error) return null

  const isNotFound = error.response?.status === 404

  if (isNotFound) {
    return (
      <Typography
        component={'p'}
        gutterBottom
        variant={'caption'}
        data-test-id="work-orders-no-wo-created"
      >
        {t('machineSentryActions.noWorkOrderCreated')}
      </Typography>
    )
  }

  return (
    <Typography
      component={'p'}
      gutterBottom
      variant={'caption'}
      color={'error'}
      data-test-id="work-orders-error"
    >
      {t('machineSentryActions.workOrderError', {errorMessage: error.message})}
    </Typography>
  )
}

export const WorkOrders: React.FC<{plantId: string; notificationId: string}> = ({
  plantId,
  notificationId
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {data, error} = useSapWorkOrders({plantId, notificationId})

  return (
    <>
      <WorkOrdersError error={error} />
      {data && (
        <Typography
          component={'p'}
          gutterBottom
          variant={'caption'}
          key={data.id}
          data-test-id={`work-order-${data.id}`}
        >
          {t('machineSentryActions.workOrder', {workOrderId: data.id})}
        </Typography>
      )}
    </>
  )
}
